import { useState, useRef, useLayoutEffect } from 'react';
import { CalendarOutlined, DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Calendar from '../calendar';
import './styles.scss';

/* ─── Quick-range shortcuts (same set used by the Incentive Calculation calendar) ── */
const RANGE_PRESETS = [
  { label: 'Today',        getRange: () => [dayjs().startOf('day').toDate(), dayjs().endOf('day').toDate()] },
  { label: 'Yesterday',    getRange: () => [dayjs().subtract(1, 'day').startOf('day').toDate(), dayjs().subtract(1, 'day').endOf('day').toDate()] },
  { label: 'This Week',    getRange: () => [dayjs().startOf('week').toDate(), dayjs().endOf('day').toDate()] },
  { label: 'Last 15 Days', getRange: () => [dayjs().subtract(14, 'day').startOf('day').toDate(), dayjs().endOf('day').toDate()] },
  { label: 'This Month',   getRange: () => [dayjs().startOf('month').toDate(), dayjs().endOf('month').toDate()] },
  { label: 'Last Month',   getRange: () => [dayjs().subtract(1, 'month').startOf('month').toDate(), dayjs().subtract(1, 'month').endOf('month').toDate()] },
  { label: 'All Time',     getRange: () => [dayjs('2000-01-01').toDate(), dayjs().endOf('day').toDate()] },
];

interface DateRangeValue { start: Date; end: Date; }

interface DateRangeFilterProps {
  value?: DateRangeValue | null;
  onChange: (range: DateRangeValue) => void;
  placeholder?: string;
}

/**
 * Trigger button + popup wrapper around the shared `Calendar` component —
 * the same date-range picker used on the Incentive Calculation page,
 * reused wherever a page needs a "from – to" filter.
 */
const DateRangeFilter = ({ value, onChange, placeholder = 'Select Date Range' }: DateRangeFilterProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [align, setAlign] = useState<'left' | 'right'>('left');
  const [vAlign, setVAlign] = useState<'below' | 'above'>('below');

  useLayoutEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);

    // Flip horizontally/vertically if the popup would overflow the viewport —
    // measured (and adjusted) before paint so there's no visible flicker.
    if (ref.current && popupRef.current) {
      const triggerRect = ref.current.getBoundingClientRect();
      const popupRect = popupRef.current.getBoundingClientRect();
      const margin = 8;
      setAlign(triggerRect.left + popupRect.width + margin > window.innerWidth ? 'right' : 'left');
      setVAlign(triggerRect.bottom + popupRect.height + margin > window.innerHeight ? 'above' : 'below');
    }

    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const label = value?.start && value?.end
    ? `${dayjs(value.start).format('DD MMM YYYY')} – ${dayjs(value.end).format('DD MMM YYYY')}`
    : placeholder;

  return (
    <div className="drf-wrap" ref={ref}>
      <button type="button" className="drf-trigger" onClick={() => setOpen(o => !o)}>
        <CalendarOutlined /> <span>{label}</span>
        <DownOutlined className="drf-caret" />
      </button>
      {open && (
        <>
          <div className="drf-popup-backdrop" onClick={() => setOpen(false)} />
          <div
            ref={popupRef}
            className={`drf-popup drf-popup-${align} drf-popup-${vAlign}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Calendar
              rangePresets={RANGE_PRESETS}
              selectedDate={value?.start && value?.end ? { start: value.start, end: value.end } : undefined}
              onDateChange={(v: any) => {
                if (v?.start && v?.end) {
                  onChange({ start: v.start, end: v.end });
                  setOpen(false);
                }
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default DateRangeFilter;
